import React, { useState, useEffect, useRef } from 'react';
import { Pencil, X, Check } from 'lucide-react';

export interface EditableCellProps {
  value: any;
  onSave: (value: any) => Promise<void>;
  type?: 'text' | 'number' | 'date' | 'select';
  options?: { value: any; label: string }[];
  disabled?: boolean;
  required?: boolean;
  className?: string;
  placeholder?: string;
}

const EditableCell: React.FC<EditableCellProps> = ({
  value,
  onSave,
  type = 'text',
  options = [],
  disabled = false,
  required = false,
  className = '',
  placeholder = '',
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>(null);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isSavingRef = useRef(false);

  // Update editValue when value prop changes
  useEffect(() => {
    setEditValue(value);
  }, [value]);

  // Focus input when editing starts
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      if (type === 'text' && inputRef.current instanceof HTMLInputElement) {
        inputRef.current.select();
      }
    }
  }, [isEditing, type]);

  const handleStartEdit = () => {
    if (disabled) return;
    setIsEditing(true);
    setEditValue(value);
    setError(null);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditValue(value);
    setError(null);
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }
  };

  const handleSave = async () => {
    if (required && (editValue === null || editValue === undefined || editValue === '')) {
      setError('Ce champ est requis');
      return;
    }

    // Validate number type
    if (type === 'number' && editValue !== '' && isNaN(Number(editValue))) {
      setError('Valeur numérique invalide');
      return;
    }

    // Clear any pending blur timeout to prevent double save
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }

    // Prevent double save by checking if already saving
    if (isSavingRef.current) {
      return;
    }

    isSavingRef.current = true;
    setIsSaving(true);
    setError(null);

    try {
      // Convert value based on type
      let valueToSave = editValue;
      if (type === 'number' && editValue !== '') {
        valueToSave = Number(editValue);
      } else if (type === 'date' && editValue) {
        valueToSave = editValue;
      }

      await onSave(valueToSave);
      setIsEditing(false);
    } catch (err: any) {
      setError(err?.error || 'Erreur lors de la sauvegarde');
    } finally {
      isSavingRef.current = false;
      setIsSaving(false);
    }
  };

  const handleBlur = () => {
    // Don't trigger blur save if we're already saving (prevents double save when clicking checkmark)
    if (isSavingRef.current) {
      return;
    }

    // Debounce save on blur (500ms)
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    saveTimeoutRef.current = setTimeout(() => {
      // Double-check that we're still editing and not saving (state might have changed)
      if (isEditing && !isSavingRef.current && editValue !== value) {
        handleSave();
      } else {
        setIsEditing(false);
      }
    }, 500);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancel();
    }
  };

  if (!isEditing) {
    return (
      <div
        className={`
          px-4 py-3 text-sm text-gray-900 cursor-pointer hover:bg-gray-50 transition-colors
          flex items-center gap-2 group
          ${disabled ? 'cursor-not-allowed opacity-50' : ''}
          ${className}
        `}
        onClick={handleStartEdit}
        title={disabled ? '' : 'Cliquer pour modifier'}
      >
        <span className="flex-1">
          {value === null || value === undefined || value === '' ? (
            <span className="text-gray-400 italic">{placeholder || 'Vide'}</span>
          ) : (
            String(value)
          )}
        </span>
        {!disabled && (
          <Pencil
            size={14}
            className="text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity"
          />
        )}
      </div>
    );
  }

  return (
    <div className={`px-4 py-3 ${className}`}>
      <div className="flex items-center gap-2">
        {type === 'select' ? (
          <select
            ref={inputRef as React.RefObject<HTMLSelectElement>}
            value={editValue || ''}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            className={`
              flex-1 px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500
              ${error ? 'border-red-500' : ''}
            `}
            disabled={isSaving}
          >
            {!required && <option value="">-- Sélectionner --</option>}
            {options.map((option) => (
              <option key={String(option.value)} value={String(option.value)}>
                {option.label}
              </option>
            ))}
          </select>
        ) : type === 'date' ? (
          <input
            ref={inputRef as React.RefObject<HTMLInputElement>}
            type="date"
            value={editValue || ''}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            className={`
              flex-1 px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500
              ${error ? 'border-red-500' : ''}
            `}
            disabled={isSaving}
          />
        ) : type === 'number' ? (
          <input
            ref={inputRef as React.RefObject<HTMLInputElement>}
            type="number"
            value={editValue ?? ''}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            className={`
              flex-1 px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500
              ${error ? 'border-red-500' : ''}
            `}
            disabled={isSaving}
            placeholder={placeholder}
          />
        ) : (
          <input
            ref={inputRef as React.RefObject<HTMLInputElement>}
            type="text"
            value={editValue ?? ''}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            className={`
              flex-1 px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500
              ${error ? 'border-red-500' : ''}
            `}
            disabled={isSaving}
            placeholder={placeholder}
          />
        )}
        <button
          onClick={handleSave}
          disabled={isSaving}
          className="p-1 text-green-600 hover:text-green-700 disabled:opacity-50"
          title="Sauvegarder (Entrée)"
        >
          <Check size={16} />
        </button>
        <button
          onClick={handleCancel}
          disabled={isSaving}
          className="p-1 text-red-600 hover:text-red-700 disabled:opacity-50"
          title="Annuler (Échap)"
        >
          <X size={16} />
        </button>
      </div>
      {error && (
        <div className="mt-1 text-xs text-red-600">{error}</div>
      )}
      {isSaving && (
        <div className="mt-1 text-xs text-gray-500">Sauvegarde...</div>
      )}
    </div>
  );
};

export default EditableCell;

